import React, {
	Dispatch,
	SetStateAction,
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
} from "react";
import { Option } from "uikit";

import { Map } from "../../../../../../../services";
import { useKeyBind } from "../../../../../../../hooks";
import { TextMemo, Text } from "../../../../../../common";
import { IField, StyledOptionList, StyledTip } from "../../styled";
import {
	buildHouseSelectOptions,
	pickHouseSelectOption,
} from "../../utils/houseSelectOptions";

const OverlayHouseBase: React.FC<OverlayHouseBase.Props> = ({
	data,
	textSelectOnSelect,
	activeId,
	setActiveOption,
	houseQuery,
}) => {
	const fieldRef = React.useRef<IField | null>(null);
	const isArrowHeld = useRef<boolean>(false);
	const styled = useMemo<Pick<Text.Props, "textStyle" | "valueStyle">>(
		() => ({
			textStyle: {
				h: "32px",
				w: "100%",
				colors: "#21333f",
				font: { fw: "400", size: "14px", line: "18px" },
				text: { tt: "uppercase" },
				cursor: "pointer",
			},
		}),
		[],
	);

	const options = useMemo(
		() => buildHouseSelectOptions(data?.houses, houseQuery),
		[data?.houses, houseQuery],
	);

	useEffect(() => {
		if (!houseQuery) return;
		const exist = data?.houses?.some((item) => item.number === houseQuery);
		if (!exist) setActiveOption(0);
	}, [data?.houses, houseQuery, setActiveOption]);

	const onClick = useCallback(
		(option: Option<string>) => {
			textSelectOnSelect(option);
		},
		[textSelectOnSelect],
	);

	const selectActive = useCallback(() => {
		const option = pickHouseSelectOption(options, activeId);
		if (option) onClick(option);
	}, [activeId, onClick, options]);

	useLayoutEffect(() => {
		if (fieldRef.current) {
			fieldRef.current?.scrollIntoView({
				behavior: isArrowHeld.current ? "auto" : "smooth",
				inline: "nearest",
				block: "nearest",
			});
		}
	}, [activeId, fieldRef]);

	useKeyBind(["ArrowUp"], (event) => {
		isArrowHeld.current = !!event?.repeat;
		setActiveOption((prev) => {
			const items = options?.length || 0;
			return prev - 1 < 0 ? items - 1 : prev - 1;
		});
	});

	useKeyBind(["ArrowDown"], (event) => {
		isArrowHeld.current = !!event?.repeat;
		setActiveOption((prev) => {
			const items = options?.length || 0;
			return prev + 1 > items - 1 ? 0 : prev + 1;
		});
	});

	useKeyBind(["Enter"], () => {
		selectActive();
	});

	useKeyBind(["Tab"], (event) => {
		selectActive();
		event.stopPropagation();
		event.preventDefault();
	});

	return (
		<StyledOptionList>
			{options?.map((option, i) => (
				<StyledTip
					ref={activeId === i ? fieldRef : null}
					key={`${i + 1}`}
					isActive={activeId === i}
					onMouseDown={() => {
						onClick(option);
						setActiveOption(i);
					}}
				>
					<TextMemo {...styled} text={option.label} />
				</StyledTip>
			))}
		</StyledOptionList>
	);
};

declare namespace OverlayHouseBase {
	interface Props {
		houseQuery: string;
		data: Map.Search.Object | undefined;
		textSelectOnSelect: (option: Option<string>) => void;
		activeId: number;
		setActiveOption: Dispatch<SetStateAction<number>>;
		debounce?: number;
	}
}

export const OverlayHouse = memo(OverlayHouseBase);
export default OverlayHouseBase;
